import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Minus, Plus, Check, Loader2, Baby, CheckCircle2 } from "lucide-react";
import Reveal from "@/components/Reveal";
import Tricolor from "@/components/Tricolor";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIME_SLOTS = [
  { value: "mattina", label: "Mattina", hours: "10:00 — 13:00" },
  { value: "pomeriggio", label: "Pomeriggio", hours: "13:00 — 17:00" },
  { value: "tutto_il_giorno", label: "Tutta la giornata", hours: "10:00 — 17:00" },
];

const initialForm = {
  full_name: "",
  email: "",
  company: "",
  role: "",
  participation: "",
  time_slot: "",
  accompanying_guests: 0,
  has_children: false,
  children_ages: [],
  notes: "",
};

const inputCls =
  "w-full rounded-xl border border-[#E3E3E0] bg-[#F7F7F5] px-4 py-3.5 text-sm text-[#2B2D2D] placeholder-[#A9ABA9] outline-none transition-colors duration-300 focus:border-[#E30613]/60 focus:bg-white";
const labelCls = "mb-2 block text-[11px] font-bold uppercase tracking-[0.25em] text-[#8A8C8A]";

const Stepper = ({ value, onChange, min, max, testId }) => (
  <div className="flex items-center gap-5" data-testid={testId}>
    <button
      type="button"
      data-testid={`${testId}-minus`}
      onClick={() => onChange(Math.max(min, value - 1))}
      disabled={value <= min}
      className="flex h-10 w-10 items-center justify-center rounded-full border border-[#DDDDD9] bg-white text-[#5B5D5D] transition-all duration-200 hover:border-[#E30613]/60 hover:text-[#E30613] disabled:opacity-30"
      aria-label="Riduci"
    >
      <Minus size={16} />
    </button>
    <span className="w-8 text-center font-display text-2xl font-semibold text-[#232525]" data-testid={`${testId}-value`}>{value}</span>
    <button
      type="button"
      data-testid={`${testId}-plus`}
      onClick={() => onChange(Math.min(max, value + 1))}
      disabled={value >= max}
      className="flex h-10 w-10 items-center justify-center rounded-full border border-[#DDDDD9] bg-white text-[#5B5D5D] transition-all duration-200 hover:border-[#E30613]/60 hover:text-[#E30613] disabled:opacity-30"
      aria-label="Aumenta"
    >
      <Plus size={16} />
    </button>
  </div>
);

const RsvpForm = () => {
  const [form, setForm] = useState(initialForm);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const setChildrenCount = (n) => {
    set({ children_ages: Array.from({ length: n }, (_, i) => form.children_ages[i] ?? "") });
  };

  const attending = form.participation === "yes";
  const totalPeople = attending ? 1 + form.accompanying_guests + form.children_ages.length : 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.email.trim() || !form.participation) {
      toast.error("Compila nome, email e partecipazione.");
      return;
    }
    if (!form.company.trim() || !form.role.trim()) {
      toast.error("Inserisci ragione sociale e ruolo.");
      return;
    }
    if (attending && !form.time_slot) {
      toast.error("Seleziona la fascia oraria preferita.");
      return;
    }
    if (attending && form.has_children && form.children_ages.some((a) => a === "" || a === null)) {
      toast.error("Indica l'età di ogni bambino.");
      return;
    }
    setSending(true);
    try {
      const payload = { ...form, children_ages: form.children_ages.map(Number) };
      const { data } = await axios.post(`${API}/rsvp`, payload);
      setResult(data);
      toast.success("RSVP inviato con successo. Grazie!");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(Array.isArray(detail) ? detail.map((d) => d.msg).join(", ") : detail || "Errore durante l'invio. Riprova.");
    } finally {
      setSending(false);
    }
  };

  const slotLabel = (v) => TIME_SLOTS.find((s) => s.value === v)?.label;

  return (
    <section id="rsvp" className="border-t border-[#ECECEA] bg-[#F1F1EF] py-28 sm:py-36" data-testid="rsvp-section">
      <div className="mx-auto max-w-3xl px-6">
        <Reveal>
          <p className="text-center text-xs font-bold uppercase tracking-[0.3em] text-[#E30613]">RSVP</p>
          <Tricolor className="mt-4 justify-center" />
          <h2 className="mt-6 text-center font-display text-2xl font-semibold uppercase tracking-[0.06em] text-[#232525] sm:text-3xl lg:text-4xl">
            Conferma la tua <span className="text-[#E30613]">partecipazione</span>
          </h2>
          <div className="mx-auto mt-6 max-w-xl space-y-3 text-center text-base font-light leading-relaxed text-[#5B5D5D]">
            <p>È gradita una conferma entro il 20 Ottobre 2026.</p>
            <p>Questo ci aiuta a garantirvi il massimo del servizio e della qualità.</p>
            <p>Indica in che fascia oraria preferisci partecipare e quante persone ti accompagneranno.</p>
            <p>Bambini al seguito? Per loro ci sarà l'animazione!</p>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="mt-14 rounded-3xl border border-[#ECECEA] bg-white p-8 shadow-[0_24px_60px_rgba(35,37,37,0.08)] sm:p-12" data-testid="rsvp-form-container">
            <AnimatePresence mode="wait">
              {result ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="py-8 text-center"
                  data-testid="rsvp-success-message"
                >
                  <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", delay: 0.15 }} className="mx-auto flex h-20 w-20 items-center justify-center rounded-full border border-[#E30613]/40 bg-[#FDECEA]">
                    <CheckCircle2 size={36} className="text-[#E30613]" />
                  </motion.div>
                  <h3 className="mt-8 font-display text-2xl font-semibold uppercase tracking-[0.05em] text-[#232525]">
                    {result.participation === "yes" ? "Grazie, ci vediamo in sede!" : "Grazie per averci risposto"}
                  </h3>
                  <p className="mt-4 text-sm font-light text-[#5B5D5D]">
                    {result.participation === "yes"
                      ? `${result.full_name} · Fascia: ${slotLabel(result.time_slot)} · Totale persone attese: ${1 + result.accompanying_guests + result.children_ages.length}`
                      : "Ci dispiace non averti con noi. Speriamo di vederti alla prossima occasione."}
                  </p>
                  <button
                    onClick={() => { setResult(null); setForm(initialForm); }}
                    data-testid="rsvp-reset-button"
                    className="mt-10 rounded-full border border-[#2B2D2D]/20 px-8 py-3 text-xs font-bold uppercase tracking-[0.25em] text-[#2B2D2D] transition-colors hover:border-[#E30613] hover:text-[#E30613]"
                  >
                    Invia un'altra risposta
                  </button>
                </motion.div>
              ) : (
                <motion.form key="form" exit={{ opacity: 0, y: -12 }} onSubmit={handleSubmit} className="space-y-8" data-testid="rsvp-form">
                  <div className="grid gap-6 sm:grid-cols-2">
                    <div>
                      <label className={labelCls} htmlFor="rsvp-name">Nome e Cognome *</label>
                      <input id="rsvp-name" data-testid="rsvp-input-fullname" className={inputCls} placeholder="es. Mario Rossi" value={form.full_name} onChange={(e) => set({ full_name: e.target.value })} required />
                    </div>
                    <div>
                      <label className={labelCls} htmlFor="rsvp-email">Indirizzo Email *</label>
                      <input id="rsvp-email" type="email" data-testid="rsvp-input-email" className={inputCls} placeholder="mario.rossi@azienda.it" value={form.email} onChange={(e) => set({ email: e.target.value })} required />
                    </div>
                    <div>
                      <label className={labelCls} htmlFor="rsvp-company">Ragione Sociale *</label>
                      <input id="rsvp-company" data-testid="rsvp-input-company" className={inputCls} placeholder="es. Officina Rossi SRL" value={form.company} onChange={(e) => set({ company: e.target.value })} required />
                    </div>
                    <div>
                      <label className={labelCls} htmlFor="rsvp-role">Ruolo *</label>
                      <input id="rsvp-role" data-testid="rsvp-input-role" className={inputCls} placeholder="es. Titolare, Responsabile tecnico..." value={form.role} onChange={(e) => set({ role: e.target.value })} required />
                    </div>
                  </div>

                  <div data-testid="rsvp-radio-participation">
                    <span className={labelCls}>Partecipazione *</span>
                    <div className="grid gap-4 sm:grid-cols-2">
                      {[
                        { value: "yes", label: "Parteciperò con piacere" },
                        { value: "no", label: "Purtroppo non potrò partecipare" },
                      ].map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          data-testid={`rsvp-participation-${opt.value}`}
                          onClick={() => set({ participation: opt.value })}
                          className={`flex items-center justify-between rounded-xl border px-5 py-4 text-left text-sm transition-all duration-300 ${
                            form.participation === opt.value
                              ? "border-[#E30613]/70 bg-[#FDECEA] text-[#232525]"
                              : "border-[#E3E3E0] bg-white text-[#5B5D5D] hover:border-[#E30613]/40"
                          }`}
                        >
                          {opt.label}
                          {form.participation === opt.value && <Check size={16} className="text-[#E30613]" />}
                        </button>
                      ))}
                    </div>
                  </div>

                  <AnimatePresence>
                    {attending && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className="space-y-8 overflow-hidden"
                        data-testid="rsvp-attending-fields"
                      >
                        <div>
                          <span className={labelCls}>Fascia Oraria Preferita *</span>
                          <div className="grid gap-3 sm:grid-cols-2" data-testid="rsvp-select-timeslot">
                            {TIME_SLOTS.map((s) => (
                              <button
                                key={s.value}
                                type="button"
                                data-testid={`rsvp-timeslot-${s.value}`}
                                onClick={() => set({ time_slot: s.value })}
                                className={`rounded-xl border px-5 py-4 text-left transition-all duration-300 ${
                                  form.time_slot === s.value
                                    ? "border-[#E30613]/70 bg-[#FDECEA]"
                                    : "border-[#E3E3E0] bg-white hover:border-[#E30613]/40"
                                }`}
                              >
                                <span className="block text-sm font-bold text-[#232525]">{s.label}</span>
                                <span className="mt-1 block text-xs font-light text-[#8A8C8A]">{s.hours}</span>
                              </button>
                            ))}
                          </div>
                        </div>

                        <div className="flex flex-col gap-4 rounded-xl border border-[#ECECEA] bg-[#F7F7F5] p-6 sm:flex-row sm:items-center sm:justify-between">
                          <div>
                            <span className="block text-sm font-bold text-[#232525]">Accompagnatori adulti</span>
                            <span className="mt-1 block text-xs font-light text-[#8A8C8A]">Quante persone parteciperanno (oltre a te)?</span>
                          </div>
                          <Stepper value={form.accompanying_guests} onChange={(v) => set({ accompanying_guests: v })} min={0} max={5} testId="rsvp-stepper-guests" />
                        </div>

                        <div className="rounded-xl border border-[#ECECEA] bg-[#F7F7F5] p-6">
                          <button
                            type="button"
                            data-testid="rsvp-checkbox-children"
                            onClick={() => set({ has_children: !form.has_children, children_ages: form.has_children ? [] : form.children_ages })}
                            className="flex w-full items-center justify-between"
                          >
                            <span className="flex items-center gap-3 text-sm font-bold text-[#232525]">
                              <Baby size={18} className="text-[#E30613]" /> Porti con te dei bambini?
                            </span>
                            <span className={`flex h-6 w-6 items-center justify-center rounded-md border transition-all duration-300 ${form.has_children ? "border-[#E30613] bg-[#E30613]" : "border-[#C9C9C5] bg-white"}`}>
                              {form.has_children && <Check size={14} className="text-white" />}
                            </span>
                          </button>

                          <AnimatePresence>
                            {form.has_children && (
                              <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: "auto" }}
                                exit={{ opacity: 0, height: 0 }}
                                className="overflow-hidden"
                                data-testid="rsvp-children-fields"
                              >
                                <div className="mt-6 flex flex-col gap-4 border-t border-[#E3E3E0] pt-6 sm:flex-row sm:items-center sm:justify-between">
                                  <span className="text-sm text-[#3A3C3C]">Quanti bambini ti accompagneranno?</span>
                                  <Stepper value={form.children_ages.length} onChange={setChildrenCount} min={0} max={6} testId="rsvp-input-children-count" />
                                </div>
                                {form.children_ages.length > 0 && (
                                  <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3">
                                    {form.children_ages.map((age, i) => (
                                      <div key={i}>
                                        <label className="mb-1.5 block text-xs text-[#8A8C8A]" htmlFor={`child-age-${i}`}>Età bambino #{i + 1}</label>
                                        <input
                                          id={`child-age-${i}`}
                                          type="number"
                                          min="0"
                                          max="17"
                                          data-testid={`rsvp-input-child-age-${i + 1}`}
                                          className={inputCls}
                                          placeholder="es. 7"
                                          value={age}
                                          onChange={(e) => {
                                            const ages = [...form.children_ages];
                                            ages[i] = e.target.value;
                                            set({ children_ages: ages });
                                          }}
                                        />
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <div>
                    <label className={labelCls} htmlFor="rsvp-notes">Note Aggiuntive</label>
                    <textarea id="rsvp-notes" rows={3} data-testid="rsvp-textarea-notes" className={`${inputCls} resize-none`} placeholder="es. Vengo con mia moglie e 2 bambini" value={form.notes} onChange={(e) => set({ notes: e.target.value })} />
                  </div>

                  {attending && (
                    <p className="text-center text-xs font-bold uppercase tracking-[0.25em] text-[#8A8C8A]" data-testid="rsvp-total-people">
                      Totale persone attese: <span className="text-[#E30613]">{totalPeople}</span>
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={sending}
                    data-testid="rsvp-submit-button"
                    className="flex w-full items-center justify-center gap-3 rounded-full bg-[#E30613] py-4 text-xs font-bold uppercase tracking-[0.25em] text-white transition-all duration-300 hover:bg-[#B00410] hover:shadow-[0_12px_32px_rgba(227,6,19,0.35)] disabled:opacity-60"
                  >
                    {sending ? <Loader2 size={16} className="animate-spin" /> : null}
                    {sending ? "Invio in corso..." : "Invia Conferma RSVP"}
                  </button>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default RsvpForm;
